/**
 * Share: a plant's photos, timeline and the gardener's words stitched into
 * one post in one tap, and the gardener's own page. Beside the evidence
 * place, the pain of photos buried in a camera roll, the heading, the body's
 * first sentence and the garden page line; the body's second sentence (friends
 * credited to you) shows only on a wide screen, where it fits.
 *
 * The picture of a plant's season on a card has not been supplied yet: it is
 * a marked place (PhotoSlot) that draws nothing in production.
 *
 * The example address is the owner's own garden, the server's
 * landing.OWNER_SLUG (facts.ownerSlug, from the landing read).
 *
 * Words (en/es): landing.share.*, landing.slots.season.
 *
 * @module Share
 */

import React from 'react';
import { SocialIcon } from '@/shared/components/SocialIcon';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { FeatureBlock } from '../components/FeatureBlock';
import { PhotoSlot } from '../components/PhotoSlot';
import { splitAround } from '../lib/splitAround';
import type { LandingSectionProps } from '../landingTypes';

/** The host a gardener's own page is on, before its "@" address. */
const PAGE_HOST = 'grow.gardengameplan.com';

/**
 * The share section.
 *
 * @param props.facts - The landing read: the owner's garden address (ownerSlug), shown as the example page.
 */
export const Share: React.FC<LandingSectionProps> = ({ facts }) => {
  const { reactTranslate: t } = useTranslation();
  const [before, after] = splitAround(t, 'landing.share.page', 'address');
  // An example of a gardener's own page: the owner's.
  const exampleAddress = `${PAGE_HOST}/@${facts.ownerSlug}`;
  return (
    <FeatureBlock
      icon={(
        <div className="flex gap-2 text-primary">
          <SocialIcon name="instagram" size={28} mono />
          <SocialIcon name="facebook" size={28} mono />
        </div>
      )}
      pain={t('landing.share.pain')}
      title={t('landing.share.title')}
      line={t('landing.share.body')}
      more={t('landing.share.bodyMore')}
      second={<>{before}<b>{exampleAddress}</b>{after}</>}
      evidence={<PhotoSlot label={t('landing.slots.season')} className="aspect-[4/3] w-full rounded-2xl shadow-xl" />}
    />
  );
};
